import { ethers, BrowserProvider, Contract } from 'ethers';
import { getPreferredProvider } from './provider';
import NoTomorrow3DABI from '../abi/NoTomorrow3D.json';
import NT3DTeamPowersABI from '../abi/NT3DTeamPowers.json';

/**
 * Contract Utilities (FOMO3D Model)
 * 
 * Helper functions for interacting with the game contract.
 * NO NT3D TOKEN - Keys themselves are dividend-bearing!
 * "The bridge between your wallet and your FOMO." 🌉
 */

// Contract addresses from environment
const GAME_ADDRESS = import.meta.env.VITE_GAME_ADDRESS || '';
const TEAM_POWERS_ADDRESS = import.meta.env.VITE_TEAM_POWERS_ADDRESS || '';
const FRAME_TOKEN_ADDRESS = import.meta.env.VITE_FRAME_TOKEN_ADDRESS || '';
const RPC_URL = import.meta.env.VITE_RPC_URL || 'https://sepolia.base.org';

// Log config on load
console.log('[NT3D Contract] Initializing with:', {
  GAME_ADDRESS,
  TEAM_POWERS_ADDRESS,
  FRAME_TOKEN_ADDRESS,
  RPC_URL
});

// Network configs
export const BASE_CONFIG = {
  chainId: '0x2105', // 8453 in hex (Base Mainnet)
  chainName: 'Base',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: ['https://mainnet.base.org'],
  blockExplorerUrls: ['https://basescan.org'],
};

// Note: Old Ethereum Sepolia config removed — project uses Base Sepolia exclusively

export const BASE_SEPOLIA_CONFIG = {
  chainId: '0x14a34', // 84532 in hex (Base Sepolia)
  chainName: 'Base Sepolia',
  nativeCurrency: {
    name: 'Sepolia Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: ['https://sepolia.base.org'],
  blockExplorerUrls: ['https://sepolia.basescan.org'],
};

// Note: PawChain and Localhost configs removed — project uses Base Sepolia exclusively

// Active network - Base Sepolia for testnet, Base Mainnet for production
export const ACTIVE_NETWORK = import.meta.env.VITE_TESTNET === 'true'
  ? BASE_SEPOLIA_CONFIG
  : import.meta.env.VITE_RPC_URL?.includes('sepolia')
  ? BASE_SEPOLIA_CONFIG
  : BASE_CONFIG;

// Dynamic block explorer URL from active network
export const BLOCK_EXPLORER_URL = ACTIVE_NETWORK.blockExplorerUrls[0] || 'https://basescan.org';

// Cached provider for read-only operations
// polling:true forces ethers to use eth_getLogs instead of eth_newFilter/eth_getFilterChanges.
// Public RPC nodes (e.g. sepolia.base.org) expire stateful filters within minutes,
// causing "filter not found" spam and dropped events.
let readProvider: ethers.JsonRpcProvider | null = null;

/**
 * Get a read-only provider (no signer needed)
 */
export function getReadProvider(): ethers.JsonRpcProvider {
  if (!readProvider) {
    readProvider = new ethers.JsonRpcProvider(RPC_URL, undefined, {
      polling: true,
      staticNetwork: true, // skip eth_chainId on every call
    });
    readProvider.pollingInterval = 4000; // 4 s — safe for public RPCs
  }
  return readProvider;
}

/**
 * Get a provider with signer from MetaMask
 */
export async function getSignerProvider(): Promise<BrowserProvider | null> {
  const eth = getPreferredProvider();
  if (!eth) {
    console.error('MetaMask not installed');
    return null;
  }

  try {
    const provider = new BrowserProvider(eth as any);
    return provider;
  } catch (error) {
    console.error('Failed to get signer provider:', error);
    return null;
  }
}

/**
 * Get the NoTomorrow3D game contract instance
 * @param withSigner If true, returns contract with signer for write operations
 */
export async function getGameContract(withSigner = false): Promise<Contract | null> {
  if (!GAME_ADDRESS) {
    console.error('Game contract address not configured');
    return null;
  }

  try {
    if (withSigner) {
      const provider = await getSignerProvider();
      if (!provider) return null;
      
      const signer = await provider.getSigner();
      return new Contract(GAME_ADDRESS, NoTomorrow3DABI.abi, signer);
    } else {
      const provider = getReadProvider();
      return new Contract(GAME_ADDRESS, NoTomorrow3DABI.abi, provider);
    }
  } catch (error) {
    console.error('Failed to get game contract:', error);
    return null;
  }
}

/**
 * Get the NT3DTeamPowers contract instance
 * @param withSigner If true, returns contract with signer for write operations
 */
export async function getTeamPowersContract(withSigner = false): Promise<Contract | null> {
  if (!TEAM_POWERS_ADDRESS) {
    console.error('Team powers contract address not configured');
    return null;
  }

  try {
    if (withSigner) {
      const provider = await getSignerProvider();
      if (!provider) return null;
      
      const signer = await provider.getSigner();
      return new Contract(TEAM_POWERS_ADDRESS, NT3DTeamPowersABI.abi, signer);
    } else {
      const provider = getReadProvider();
      return new Contract(TEAM_POWERS_ADDRESS, NT3DTeamPowersABI.abi, provider);
    }
  } catch (error) {
    console.error('Failed to get team powers contract:', error);
    return null;
  }
}

/**
 * Get team powers contract address
 */
export function getTeamPowersAddress(): string {
  return TEAM_POWERS_ADDRESS;
}

/**
 * Event listener setup for real-time updates
 */
interface EventCallbacks {
  onKeyPurchased?: (buyer: string, amount: bigint, keys: bigint, team: number, event: any) => void;
  onRoundEnded?: (roundId: bigint, winner: string, amount: bigint, event: any) => void;
  onJackpotTriggered?: (roundId: bigint, tier: number, percentage: number, amount: bigint, event: any) => void;
  onTimerExtended?: (newDeadline: bigint, addedTime: bigint, event: any) => void;
  onDividendsWithdrawn?: (player: string, amount: bigint, event: any) => void;
}

/**
 * Poll for contract events using queryFilter (eth_getLogs) instead of
 * eth_newFilter / eth_getFilterChanges.  Public RPC nodes expire stateful
 * filters quickly, causing constant "filter not found" errors and silent
 * event drops.  queryFilter is stateless and works reliably on any node.
 */
export function setupEventListeners(callbacks: EventCallbacks): () => void {
  let stopped = false;
  let lastBlock = 0;

  const poll = async () => {
    if (stopped) return;
    try {
      const provider = getReadProvider();
      const gameContract = await getGameContract();
      if (!gameContract) return;

      const currentBlock = await provider.getBlockNumber();
      if (lastBlock === 0) {
        // On first run, only look at the most recent block to avoid replaying history
        lastBlock = currentBlock - 1;
      }

      if (currentBlock <= lastBlock) return;

      const fromBlock = lastBlock + 1;
      const toBlock = currentBlock;
      lastBlock = currentBlock;

      const eventNames: Array<{ name: string; cb: keyof EventCallbacks }> = [
        { name: 'KeyPurchased',      cb: 'onKeyPurchased' },
        { name: 'RoundEnded',        cb: 'onRoundEnded' },
        { name: 'JackpotTriggered',  cb: 'onJackpotTriggered' },
        { name: 'TimerExtended',     cb: 'onTimerExtended' },
        { name: 'DividendsWithdrawn',cb: 'onDividendsWithdrawn' },
      ];

      await Promise.all(
        eventNames
          .filter(({ cb }) => !!callbacks[cb])
          .map(async ({ name, cb }) => {
            try {
              const logs = await gameContract.queryFilter(name, fromBlock, toBlock);
              for (const log of logs) {
                const handler = callbacks[cb] as Function;
                handler(...(log as any).args, log);
              }
            } catch (e) {
              // Silently ignore per-event fetch errors (e.g. event not in ABI)
            }
          })
      );
    } catch (e) {
      // Ignore transient RPC errors between polls
    }
  };

  const POLL_MS = 4000;
  const interval = setInterval(poll, POLL_MS);
  poll(); // immediate first run

  return () => {
    stopped = true;
    clearInterval(interval);
  };
}

/**
 * Switch to the active network (Base Sepolia for testnet, Base Mainnet for production)
 */
export async function switchToActiveNetwork(): Promise<boolean> {
  const eth = getPreferredProvider();
  if (!eth) return false;

  try {
    await eth.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: ACTIVE_NETWORK.chainId }],
    });
    return true;
  } catch (switchError: any) {
    // Chain not added, try to add it
    if (switchError.code === 4902) {
      try {
        await eth.request({
          method: 'wallet_addEthereumChain',
          params: [ACTIVE_NETWORK],
        });
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
}

/**
 * Check if user is on the correct network
 */
export async function isCorrectNetwork(): Promise<boolean> {
  const eth = getPreferredProvider();
  if (!eth) return false;
  
  try {
    const chainId = await eth.request({ method: 'eth_chainId' });
    return chainId === ACTIVE_NETWORK.chainId;
  } catch {
    return false;
  }
}

/**
 * Get Frame token address
 */
export function getFrameTokenAddress(): string {
  return FRAME_TOKEN_ADDRESS;
}

/**
 * Get game contract address
 */
export function getGameAddress(): string {
  return GAME_ADDRESS;
}

// Standard ERC20 ABI for Frame token interactions
const ERC20_ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function balanceOf(address account) external view returns (uint256)',
  'function transfer(address to, uint256 amount) external returns (bool)',
  'function decimals() external view returns (uint8)',
  'function symbol() external view returns (string)',
];

/**
 * Get the PAW token contract instance (ERC20)
 * @param withSigner If true, returns contract with signer for write operations
 */
export async function getFrameContract(withSigner = false): Promise<Contract | null> {
  if (!FRAME_TOKEN_ADDRESS) {
    console.error('Frame token address not configured');
    return null;
  }

  try {
    if (withSigner) {
      const provider = await getSignerProvider();
      if (!provider) return null;
      
      const signer = await provider.getSigner();
      return new Contract(FRAME_TOKEN_ADDRESS, ERC20_ABI, signer);
    } else {
      const provider = getReadProvider();
      return new Contract(FRAME_TOKEN_ADDRESS, ERC20_ABI, provider);
    }
  } catch (error) {
    console.error('Failed to get Frame contract:', error);
    return null;
  }
}

/**
 * Withdraw pending dividends for a user
 */
export async function withdrawDividends(): Promise<boolean> {
  try {
    const contract = await getGameContract(true);
    if (!contract) return false;
    
    const tx = await contract.withdrawDividends();
    await tx.wait();
    return true;
  } catch (error) {
    console.error('Failed to withdraw dividends:', error);
    return false;
  }
}

/**
 * Get pending dividends for a user
 */
export async function getPendingDividends(userAddress: string): Promise<bigint> {
  try {
    const contract = await getGameContract();
    if (!contract) return 0n;
    
    return await contract.withdrawableDividendsOf(userAddress);
  } catch (error) {
    console.error('Failed to get pending dividends:', error);
    return 0n;
  }
}

/**
 * Get user's key balance
 */
export async function getKeyBalance(userAddress: string): Promise<bigint> {
  try {
    const contract = await getGameContract();
    if (!contract) return 0n;
    
    return await contract.keyBalance(userAddress);
  } catch (error) {
    console.error('Failed to get key balance:', error);
    return 0n;
  }
}

/**
 * Get current Reaper Key price from contract
 * Uses getReaperPrice() from ViewFacet with inverse curve pricing
 */
export async function getReaperPrice(): Promise<bigint> {
  try {
    const contract = await getGameContract();
    if (!contract) return 0n;
    
    // Try to use contract's getReaperPrice() first (added in 2025 upgrade)
    try {
      const price = await contract.getReaperPrice();
      return BigInt(price);
    } catch (e) {
      // Fallback: calculate client-side if contract function not available
      console.warn('getReaperPrice: Falling back to client-side calculation');
    }
    
    // Fallback calculation
    const [roundInfo, keyPrice] = await Promise.all([
      contract.getRoundInfo(),
      contract.getFullKeyPrice()
    ]);
    
    const deadline = BigInt(roundInfo.deadline);
    const now = BigInt(Math.floor(Date.now() / 1000));
    
    if (now >= deadline) return 0n;
    
    const timeRemaining = deadline - now;
    if (timeRemaining === 0n) return 0n;
    
    // Constants from contract
    const REAPER_PRICE_MULTIPLIER = 5n;
    const BASE_TIMER = 86400n; // 24 hours
    
    // Formula: (5 * keyPrice * 86400) / timeRemaining
    return (REAPER_PRICE_MULTIPLIER * BigInt(keyPrice) * BASE_TIMER) / timeRemaining;
  } catch (error) {
    console.error('Failed to calculate reaper price:', error);
    return 0n;
  }
}

/**
 * Buy a Reaper Key
 * Uses buyReaperKey() from GameCoreFacet
 */
export async function buyReaperKey(): Promise<boolean> {
  try {
    const contract = await getGameContract(true);
    if (!contract) throw new Error('Game contract not available');
    
    // Check if buyReaperKey function exists
    if (typeof contract.buyReaperKey !== 'function') {
      console.warn('buyReaperKey: Function not found in contract ABI');
      throw new Error('Reaper Keys are not available in the current game version');
    }
    
    const tx = await contract.buyReaperKey();
    await tx.wait();
    return true;
  } catch (error: any) {
    console.error('Failed to buy reaper key:', error);
    throw error;
  }
}

/**
 * End the round if the timer has expired and start a new round
 * This can be called by anyone when the round has ended
 */
export async function endRoundIfExpired(): Promise<boolean> {
  try {
    const contract = await getGameContract(true);
    if (!contract) return false;
    
    const tx = await contract.endRoundIfExpired();
    await tx.wait();
    return true;
  } catch (error) {
    console.error('Failed to end round:', error);
    return false;
  }
}

/**
 * Check if the current round has ended (timer expired but not finalized)
 */
export async function isRoundExpired(): Promise<boolean> {
  try {
    const contract = await getGameContract();
    if (!contract) return false;
    
    const roundInfo = await contract.getRoundInfo();
    const timeRemaining = Number(roundInfo.timeRemaining || 0);
    const ended = roundInfo.ended || false;
    
    // Round is expired if time is 0 and hasn't been ended yet
    return timeRemaining === 0 && !ended;
  } catch (error) {
    console.error('Failed to check round status:', error);
    return false;
  }
}

// ============================================================================
// TEAM POWERS FUNCTIONS
// ============================================================================

/**
 * Team power status interfaces (using new faction names)
 */
export interface RavenStatus {
  multiplier: number; // Percentage multiplier (e.g., 102 = 2% bonus)
}

export interface ApeRunStatus {
  consecutiveBuys: number;
  bonusPool: bigint;
  stampedePayout: bigint;
}

export interface RabbitFrenzyStatus {
  isActive: boolean;
  totalPayout: bigint;
  topRabbits: string[];
}

export interface WolfPackStatus {
  goal1Reached: boolean;
  goal2Reached: boolean;
  currentBonus: number;
  rescueFund: bigint;
}

export interface ViperLotteryStatus {
  entryCount: number;
  lotteryPool: bigint;
}

export interface OwlProphecyStatus {
  prophecyMade: boolean;
  predictedOutcome: number;
  blessingUsed: boolean;
  blessedTeam: number;
}

export interface FoxBountyStatus {
  isActive: boolean;
  bountyType: number;
  rewardKeys: bigint;
  claimed: boolean;
}

export interface TeamJackpotStatus {
  totalPool: bigint;
  distributed: boolean;
}

// Legacy type aliases for backward compatibility
export type WhaleStatus = RavenStatus;
export type BullRunStatus = ApeRunStatus;
export type SharkFrenzyStatus = RabbitFrenzyStatus;
export type DolphinPodStatus = WolfPackStatus;
export type SnakeLotteryStatus = ViperLotteryStatus;
export type EagleProphecyStatus = OwlProphecyStatus;
export type TigerBountyStatus = FoxBountyStatus;

/**
 * Get Raven team multiplier for a round
 */
export async function getRavenMultiplier(roundId: number): Promise<RavenStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { multiplier: 100 };
    
    const multiplier = await contract.getRavenMultiplier(roundId);
    return { multiplier: Number(multiplier) };
  } catch (error) {
    console.error('Failed to get raven multiplier:', error);
    return { multiplier: 100 };
  }
}

// Legacy alias
export const getWhaleMultiplier = getRavenMultiplier;

/**
 * Get Ape Run status for a round
 */
export async function getApeRunStatus(roundId: number): Promise<ApeRunStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { consecutiveBuys: 0, bonusPool: 0n, stampedePayout: 0n };
    
    const [consecutiveBuys, bonusPool, stampedePayout] = await contract.getApeRunStatus(roundId);
    return {
      consecutiveBuys: Number(consecutiveBuys),
      bonusPool,
      stampedePayout
    };
  } catch (error) {
    console.error('Failed to get ape run status:', error);
    return { consecutiveBuys: 0, bonusPool: 0n, stampedePayout: 0n };
  }
}

// Legacy alias
export const getBullRunStatus = getApeRunStatus;

/**
 * Get Rabbit Frenzy status for a round
 */
export async function getRabbitFrenzyStatus(roundId: number): Promise<RabbitFrenzyStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { isActive: false, totalPayout: 0n, topRabbits: [] };
    
    const [isActive, totalPayout, topRabbits] = await contract.getRabbitFrenzyStatus(roundId);
    return {
      isActive,
      totalPayout,
      topRabbits: topRabbits.filter((s: string) => s !== ethers.ZeroAddress)
    };
  } catch (error) {
    console.error('Failed to get rabbit frenzy status:', error);
    return { isActive: false, totalPayout: 0n, topRabbits: [] };
  }
}

// Legacy alias
export const getSharkFrenzyStatus = getRabbitFrenzyStatus;

/**
 * Get Wolf Pack status for a round
 */
export async function getWolfPackStatus(roundId: number): Promise<WolfPackStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { goal1Reached: false, goal2Reached: false, currentBonus: 0, rescueFund: 0n };
    
    const [goal1Reached, goal2Reached, currentBonus, rescueFund] = await contract.getWolfPackStatus(roundId);
    return {
      goal1Reached,
      goal2Reached,
      currentBonus: Number(currentBonus),
      rescueFund
    };
  } catch (error) {
    console.error('Failed to get wolf pack status:', error);
    return { goal1Reached: false, goal2Reached: false, currentBonus: 0, rescueFund: 0n };
  }
}

// Legacy alias
export const getDolphinPodStatus = getWolfPackStatus;

/**
 * Get Viper Lottery info for a round
 */
export async function getViperLotteryInfo(roundId: number): Promise<ViperLotteryStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { entryCount: 0, lotteryPool: 0n };
    
    const [entryCount, lotteryPool] = await contract.getViperLotteryInfo(roundId);
    return {
      entryCount: Number(entryCount),
      lotteryPool
    };
  } catch (error) {
    console.error('Failed to get viper lottery info:', error);
    return { entryCount: 0, lotteryPool: 0n };
  }
}

// Legacy alias
export const getSnakeLotteryInfo = getViperLotteryInfo;

/**
 * Get Owl Prophecy status for a round
 */
export async function getOwlProphecyStatus(roundId: number): Promise<OwlProphecyStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { prophecyMade: false, predictedOutcome: 0, blessingUsed: false, blessedTeam: 0 };
    
    const [prophecyMade, predictedOutcome, blessingUsed, blessedTeam] = await contract.getOwlProphecyStatus(roundId);
    return {
      prophecyMade,
      predictedOutcome: Number(predictedOutcome),
      blessingUsed,
      blessedTeam: Number(blessedTeam)
    };
  } catch (error) {
    console.error('Failed to get owl prophecy status:', error);
    return { prophecyMade: false, predictedOutcome: 0, blessingUsed: false, blessedTeam: 0 };
  }
}

// Legacy alias
export const getEagleProphecyStatus = getOwlProphecyStatus;

/**
 * Get current Fox Bounty for a round
 */
export async function getCurrentFoxBounty(roundId: number): Promise<FoxBountyStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { isActive: false, bountyType: 0, rewardKeys: 0n, claimed: false };
    
    const [isActive, bountyType, rewardKeys, claimed] = await contract.getCurrentFoxBounty(roundId);
    return {
      isActive,
      bountyType: Number(bountyType),
      rewardKeys,
      claimed
    };
  } catch (error) {
    console.error('Failed to get fox bounty:', error);
    return { isActive: false, bountyType: 0, rewardKeys: 0n, claimed: false };
  }
}

// Legacy alias
export const getCurrentTigerBounty = getCurrentFoxBounty;

/**
 * Get Team Jackpot info for a round
 */
export async function getTeamJackpotInfo(roundId: number): Promise<TeamJackpotStatus> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return { totalPool: 0n, distributed: false };
    
    const [totalPool, distributed] = await contract.getTeamJackpotInfo(roundId);
    return { totalPool, distributed };
  } catch (error) {
    console.error('Failed to get team jackpot info:', error);
    return { totalPool: 0n, distributed: false };
  }
}

/**
 * Get player's claimable team jackpot share
 */
export async function getPlayerTeamJackpotShare(
  roundId: number,
  teamId: number,
  playerKeys: bigint,
  teamKeys: bigint
): Promise<bigint> {
  try {
    const contract = await getTeamPowersContract();
    if (!contract) return 0n;
    
    return await contract.getPlayerTeamJackpotShare(roundId, teamId, playerKeys, teamKeys);
  } catch (error) {
    console.error('Failed to get player team jackpot share:', error);
    return 0n;
  }
}

/**
 * Claim team jackpot winnings for a specific round
 */
export async function claimTeamJackpot(roundId: number): Promise<boolean> {
  try {
    const contract = await getTeamPowersContract(true);
    if (!contract) return false;
    
    const tx = await contract.claimTeamJackpot(roundId);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('Failed to claim team jackpot:', error);
    throw error;
  }
}

/**
 * Get all team power statuses for a round
 */
export interface AllTeamPowerStatuses {
  raven: RavenStatus;
  ape: ApeRunStatus;
  rabbit: RabbitFrenzyStatus;
  wolf: WolfPackStatus;
  viper: ViperLotteryStatus;
  owl: OwlProphecyStatus;
  fox: FoxBountyStatus;
  jackpot: TeamJackpotStatus;
  // Legacy aliases for backward compatibility
  whale: RavenStatus;
  bull: ApeRunStatus;
  shark: RabbitFrenzyStatus;
  dolphin: WolfPackStatus;
  snake: ViperLotteryStatus;
  eagle: OwlProphecyStatus;
  tiger: FoxBountyStatus;
}

export async function getAllTeamPowerStatuses(roundId: number): Promise<AllTeamPowerStatuses> {
  const [raven, ape, rabbit, wolf, viper, owl, fox, jackpot] = await Promise.all([
    getRavenMultiplier(roundId),
    getApeRunStatus(roundId),
    getRabbitFrenzyStatus(roundId),
    getWolfPackStatus(roundId),
    getViperLotteryInfo(roundId),
    getOwlProphecyStatus(roundId),
    getCurrentFoxBounty(roundId),
    getTeamJackpotInfo(roundId)
  ]);

  return {
    raven, ape, rabbit, wolf, viper, owl, fox, jackpot,
    // Legacy aliases
    whale: raven, bull: ape, shark: rabbit, dolphin: wolf,
    snake: viper, eagle: owl, tiger: fox
  };
}
